import type { User } from '../api';

export type SidebarSection = 'new-game' | 'history';

type SidebarProps = {
  user: User;
  title?: string;
  active: SidebarSection;
  onNewGame: () => void;
  onHistory: () => void;
  onLogout: () => void;
  // mobile: the sidebar slides in as an overlay drawer with a close button
  open?: boolean;
  onClose?: () => void;
};

function SidebarIcon({ name }: { name: 'game' | 'plus' | 'history' | 'logout' }) {
  switch (name) {
    case 'game':
      return (
        <svg className="sidebar__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4C6.48 4 2 8.48 2 14v2c0 2.21 1.79 4 4 4 .83 0 1.61-.26 2.27-.7L9.6 18h4.8l1.33 1.3c.66.44 1.44.7 2.27.7 2.21 0 4-1.79 4-4v-2c0-5.52-4.48-10-10-10zm8 12c0 1.1-.9 2-2 2-.42 0-.81-.13-1.14-.36l-1.53-1.5H8.67l-1.53 1.5c-.33.23-.72.36-1.14.36-1.1 0-2-.9-2-2v-2c0-4.41 3.59-8 8-8s8 3.59 8 8v2z" />
        </svg>
      );
    case 'plus':
      return (
        <svg className="sidebar__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
        </svg>
      );
    case 'history':
      return (
        <svg className="sidebar__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.05 4.95l-1.41 1.41A9 9 0 1 0 13 3zm-1 5v5l4.28 2.62.72-1.21-3.5-2.12V8H12z" />
        </svg>
      );
    case 'logout':
      return (
        <svg className="sidebar__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M16 17v-3H9v-4h7V7l5 5-5 5zm-1 2h-2v4H4V3h9v4h2V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3z" />
        </svg>
      );
  }
}

export function Sidebar({ user, title = 'TayTayA', active, onNewGame, onHistory, onLogout, open = false, onClose }: SidebarProps) {
  return (
    <aside id="sidebar" className={`sidebar ${open ? 'sidebar--open' : ''}`}>
      <div className="sidebar__brand">
        <span className="sidebar__brand-icon">
          <SidebarIcon name="game" />
        </span>
        <span className="sidebar__brand-text">{title}</span>
        {onClose && (
          <button type="button" className="sidebar__close" onClick={onClose} aria-label="Close menu">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        )}
      </div>

      <nav className="sidebar__nav" aria-label="Main navigation">
        <button
          type="button"
          className={`sidebar__nav-item sidebar__nav-item--new ${active === 'new-game' ? 'sidebar__nav-item--active' : ''}`}
          onClick={onNewGame}
          title="Start a new game"
        >
          <SidebarIcon name="plus" />
          <span className="sidebar__label">New Game</span>
        </button>
        <button
          type="button"
          className={`sidebar__nav-item ${active === 'history' ? 'sidebar__nav-item--active' : ''}`}
          onClick={onHistory}
          title="View game history"
        >
          <SidebarIcon name="history" />
          <span className="sidebar__label">History</span>
        </button>
      </nav>

      <div className="sidebar__footer">
        <div className="sidebar__user">
          <span className="sidebar__user-avatar">{user.username.charAt(0).toUpperCase()}</span>
          <span className="sidebar__label sidebar__user-name">{user.username}</span>
        </div>
        <button type="button" className="sidebar__nav-item sidebar__nav-item--logout" onClick={onLogout}>
          <SidebarIcon name="logout" />
          <span className="sidebar__label">Logout</span>
        </button>
      </div>
      <div className="sidebar__version">Beta v0.1.0</div>
    </aside>
  );
}
